// Promotions (staff). Define the offer — a single % or tiered by order value — and who gets it, then
// watch how it performs. Admins/managers create and edit; every staff member can read and see results.
// Promotions never email customers on their own: announcements are written and sent from Marketing.
import { useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import {
  fetchDiscounts,
  createDiscount,
  updateDiscount,
  setDiscountActive,
  setDiscountArchived,
  deleteDiscount,
  fetchDiscountReport,
  previewDiscount,
  fetchThresholds,
  fetchSegment,
} from '../api/discounts.js';
import { fetchCompanies } from '../api/companies.js';
import { createCampaign, fetchPromotionCampaigns } from '../api/marketing.js';
import { useNavigate, Link } from 'react-router-dom';
import { storeShort } from '../lib/stores.js';
import { fmtMoney } from '../lib/matrix.js';
import { useAuthStore } from '../stores/authStore.js';
import { can, CAP, allowedStoreCodes } from '../lib/access.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const CUR = { A: 'AUD', B: 'USD' };
const field = 'mt-1 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';
const chip = (on) => `rounded-full px-3 py-1 text-sm ${on ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'}`;

const ZONES = [
  ['Australia/Sydney', 'Sydney'],
  ['Pacific/Auckland', 'Auckland'],
  ['Europe/London', 'London'],
  ['America/New_York', 'New York'],
  ['America/Los_Angeles', 'Los Angeles'],
  ['UTC', 'UTC'],
];
const zoneLabel = (tz) => ZONES.find(([z]) => z === tz)?.[1] || tz;
const fmtWhole = (n, cur) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n} ${cur}`;
  }
};
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '');

const BLANK = {
  id: null,
  store: 'A',
  bothStores: false,
  name: '',
  description: '',
  type: 'SINGLE',
  percent: '',
  tiers: [{ minSubtotal: '', percent: '' }],
  currencyMode: '', // TIERED: 'CONVERT' | 'SAME' — deliberately unset so the creator has to choose
  allCustomers: false,
  companyIds: [],
  excludedCompanyIds: [],
  audienceLabel: '',
  timeZone: 'Australia/Sydney',
  startDate: '',
  endDate: '',
  active: true,
};

/** Live | Scheduled | Ended | Paused | Archived — what a promotion is doing right now. */
export function promoStatus(d, now = new Date()) {
  if (d.archivedAt) return { key: 'archived', label: 'Archived', cls: 'bg-surface-raised text-text-muted' };
  if (!d.active) return { key: 'paused', label: 'Paused', cls: 'bg-warning-light text-warning' };
  if (d.startsAt && new Date(d.startsAt) > now) return { key: 'scheduled', label: 'Scheduled', cls: 'bg-accent-light text-accent' };
  if (d.endsAt && new Date(d.endsAt) < now) return { key: 'ended', label: 'Ended', cls: 'bg-surface-raised text-text-muted' };
  return { key: 'live', label: 'Live', cls: 'bg-success-light text-success' };
}

function scheduleLabel(d) {
  const z = d.timeZone && d.timeZone !== 'Australia/Sydney' ? ` (${zoneLabel(d.timeZone)})` : '';
  if (d.startDate && d.endDate) return `${fmtDay(d.startDate)} – ${fmtDay(d.endDate)}${z}`;
  if (d.startDate) return `from ${fmtDay(d.startDate)}${z}`;
  if (d.endDate) return `until ${fmtDay(d.endDate)}${z}`;
  return 'no end date';
}

// Shared shape → API body (for save, preview and the threshold table).
function toBody(form) {
  const body = {
    store: form.store,
    name: form.name.trim(),
    description: form.description.trim(),
    type: form.type,
    allCustomers: form.allCustomers,
    companyIds: form.allCustomers ? [] : form.companyIds,
    excludedCompanyIds: form.allCustomers ? form.excludedCompanyIds : [],
    audienceLabel: form.allCustomers ? null : form.audienceLabel || null,
    timeZone: form.timeZone,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    active: form.active,
  };
  if (form.type === 'SINGLE') body.percent = Number(form.percent) || 0;
  else {
    body.tiers = form.tiers
      .filter((t) => t.percent !== '' && t.minSubtotal !== '')
      .map((t) => ({ minSubtotal: Number(t.minSubtotal) || 0, percent: Number(t.percent) || 0 }));
    if (form.currencyMode) body.currencyMode = form.currencyMode;
  }
  return body;
}

// --- "How clients in other currencies see the tiers" ----------------------------------------------
function ThresholdTable({ form, store }) {
  const [rows, setRows] = useState(null);
  const body = toBody({ ...form, store });
  const key = JSON.stringify([store, body.tiers, body.currencyMode]);
  useEffect(() => {
    if (form.type !== 'TIERED' || !body.tiers?.length || !body.currencyMode) return setRows(null);
    const t = setTimeout(() => fetchThresholds(body).then(setRows).catch(() => setRows(null)), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!rows?.length) return null;
  return (
    <div className="mt-2 rounded-md bg-surface-raised/60 p-2.5 text-xs">
      <div className="mb-1 font-medium text-text-secondary">What {storeShort(store)} clients will see</div>
      <table className="w-full">
        <tbody>
          {rows.map((r) => (
            <tr key={r.currency}>
              <td className="py-0.5 pr-3 text-text-muted">
                {r.currency} · {r.clients} client{r.clients === 1 ? '' : 's'}
              </td>
              <td className="py-0.5 tabular-nums text-text-primary">
                {r.tiers.map((t) => (t.minSubtotal == null ? '—' : `${fmtWhole(t.minSubtotal, r.currency)} → ${t.percent}%`)).join('   ·   ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Segment builder: find customers by rule and add them to the selection --------------------------
function SegmentBuilder({ stores, companies, onAdd }) {
  const reps = useMemo(() => {
    const m = new Map();
    for (const c of companies) for (const a of c.repAssignments || []) if (a.repUser) m.set(a.repUser.id, a.repUser);
    return [...m.values()].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  }, [companies]);
  const currencies = useMemo(
    () => [...new Set(companies.flatMap((c) => (c.locations || []).map((l) => l.currencyCode)).filter(Boolean))].sort(),
    [companies]
  );
  const [rules, setRules] = useState({ repUserIds: [], currencies: [], minLifetimeSpend: '', noOrderDays: '', neverOrdered: false });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (k, v) => setRules((r) => ({ ...r, [k]: r[k].includes(v) ? r[k].filter((x) => x !== v) : [...r[k], v] }));

  const find = async () => {
    setBusy(true);
    try {
      const body = {
        repUserIds: rules.repUserIds,
        currencies: rules.currencies,
        ...(rules.minLifetimeSpend ? { minLifetimeSpend: Number(rules.minLifetimeSpend) } : {}),
        ...(rules.noOrderDays ? { noOrderDays: Number(rules.noOrderDays) } : {}),
        neverOrdered: rules.neverOrdered,
      };
      const parts = await Promise.all(stores.map((store) => fetchSegment({ ...body, store })));
      setResult({ companies: parts.flatMap((p) => p.companies), label: parts[0]?.label || '' });
    } catch {
      setResult({ companies: [], label: '', error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-line bg-surface-raised/40 p-3 text-sm">
      <div className="text-xs font-medium text-text-muted">Find customers by…</div>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        {reps.length > 0 && (
          <div>
            <div className="text-xs text-text-muted">Rep</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {reps.map((r) => (
                <button key={r.id} type="button" onClick={() => toggle('repUserIds', r.id)} className={chip(rules.repUserIds.includes(r.id))}>
                  {r.name || r.email}
                </button>
              ))}
            </div>
          </div>
        )}
        {currencies.length > 1 && (
          <div>
            <div className="text-xs text-text-muted">Buys in</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {currencies.map((c) => (
                <button key={c} type="button" onClick={() => toggle('currencies', c)} className={chip(rules.currencies.includes(c))}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="block text-xs text-text-muted">
          Lifetime spend at least ({stores.length > 1 ? 'store currency' : CUR[stores[0]]})
          <input type="number" min={0} step="1000" value={rules.minLifetimeSpend} onChange={(e) => setRules({ ...rules, minLifetimeSpend: e.target.value })} placeholder="any" className={field} />
        </label>
        <div className="grid grid-cols-[1fr_auto] items-end gap-3">
          <label className="block text-xs text-text-muted">
            No order in the last … days
            <input type="number" min={1} step="30" value={rules.noOrderDays} disabled={rules.neverOrdered} onChange={(e) => setRules({ ...rules, noOrderDays: e.target.value })} placeholder="any" className={field} />
          </label>
          <label className="flex items-center gap-1.5 pb-2 text-xs text-text-secondary">
            <input type="checkbox" checked={rules.neverOrdered} onChange={(e) => setRules({ ...rules, neverOrdered: e.target.checked, noOrderDays: '' })} className="accent-accent" />
            Never ordered
          </label>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={find} disabled={busy} className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
          {busy ? 'Finding…' : 'Find matching customers'}
        </button>
        {result && !result.error && (
          <>
            <span className="text-xs text-text-muted">
              {result.companies.length} match{result.companies.length === 1 ? '' : 'es'}
              {result.label ? ` · ${result.label}` : ''}
            </span>
            {result.companies.length > 0 && (
              <button type="button" onClick={() => onAdd(result.companies.map((c) => c.id), result.label)} className="text-sm font-medium text-accent hover:underline">
                Add {result.companies.length} to selection
              </button>
            )}
          </>
        )}
        {result?.error && <span className="text-xs text-error">Couldn’t run that search.</span>}
      </div>
    </div>
  );
}

// --- Searchable customer checklist (selection or exclusions) ---------------------------------------
function CompanyChecklist({ companies, selected, onToggle, showStore, onSelectAll, onClear }) {
  const [q, setQ] = useState('');
  const shown = companies.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <>
      <div className="border-b border-line p-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="w-full rounded border border-line px-2.5 py-1.5 text-sm outline-none focus:border-accent" />
      </div>
      <div className="max-h-56 overflow-y-auto p-1">
        {shown.length === 0 ? (
          <div className="px-3 py-3 text-xs text-text-muted">No customers.</div>
        ) : (
          shown.map((c) => (
            <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-surface-raised">
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => onToggle(c.id)} className="accent-accent" />
              <span className="min-w-0 flex-1 truncate text-text-primary">{c.name}</span>
              {showStore && <span className="text-[11px] text-text-muted">{storeShort(c.store?.code || c.store)}</span>}
              {!c.isActive && <span className="text-[11px] text-text-muted">inactive</span>}
            </label>
          ))
        )}
      </div>
      <div className="flex justify-between border-t border-line px-3 py-1.5 text-xs text-text-muted">
        <span>{selected.length} selected</span>
        <span className="flex gap-3">
          {onSelectAll && <button type="button" onClick={() => onSelectAll(shown.map((c) => c.id))} className="text-accent hover:underline">Select shown</button>}
          {selected.length > 0 && <button type="button" onClick={onClear} className="text-error hover:underline">Clear</button>}
        </span>
      </div>
    </>
  );
}

// --- "What would this client get?" ----------------------------------------------------------------
function PreviewBox({ form, companies }) {
  const [companyId, setCompanyId] = useState('');
  const [amount, setAmount] = useState('');
  const [res, setRes] = useState(null);
  const pool = form.allCustomers ? companies.filter((c) => !form.excludedCompanyIds.includes(c.id)) : companies.filter((c) => form.companyIds.includes(c.id));
  const list = pool.length ? pool : companies;
  const co = companies.find((c) => c.id === companyId);
  const coCurrency = co?.locations?.[0]?.currencyCode || CUR[co?.store?.code || form.store];

  const run = async () => {
    if (!companyId || amount === '') return;
    const store = co?.store?.code || form.store;
    try {
      setRes(await previewDiscount({ ...toBody({ ...form, store }), id: form.id || undefined, companyId, subtotal: Number(amount) }));
    } catch (e) {
      setRes({ error: errMsg(e) });
    }
  };

  return (
    <div className="mt-4 rounded-md border border-line p-3">
      <div className="text-xs font-medium text-text-muted">Try it before you launch</div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setRes(null); }} className="min-w-0 max-w-[16rem] rounded-md border border-line bg-surface px-2 py-1.5 text-sm">
          <option value="">Pick a customer…</option>
          {list.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <span className="text-text-muted">orders</span>
        <input type="number" min={0} step="50" value={amount} onChange={(e) => { setAmount(e.target.value); setRes(null); }} placeholder="1000" className="w-28 rounded-md border border-line px-2 py-1.5 text-sm" />
        <span className="text-text-muted">{companyId ? coCurrency : ''}</span>
        <button type="button" onClick={run} disabled={!companyId || amount === ''} className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-40">
          Check
        </button>
      </div>
      {res && (
        <p className={`mt-2 text-sm ${res.error ? 'text-error' : 'text-text-primary'}`}>
          {res.error
            ? res.error
            : !res.targeted
              ? `${res.company} isn’t included in this promotion.`
              : res.percent === 0
                ? `${res.company} wouldn’t reach a tier on ${fmtMoney(res.subtotal, res.currency)}.`
                : `${res.company} gets ${res.percent}% off — saves ${fmtMoney(res.saving, res.currency)}.`}
          {res.otherPromotion && !res.error && (
            <span className="block text-xs text-text-muted">
              They already have “{res.otherPromotion.name}” at {res.otherPromotion.percent}% on this order — {res.wins ? 'this promotion is better, so it applies.' : 'that one is better, so it would still apply.'}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function DiscountForm({ initial, companies, storeOptions, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const stores = form.bothStores ? ['A', 'B'] : [form.store];
  const storeCompanies = useMemo(() => companies.filter((c) => stores.includes(c.store?.code || c.store)), [companies, form.store, form.bothStores]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTier = (i, patch) => set({ tiers: form.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const toggleIn = (key, id) => set({ [key]: form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id] });

  const save = async () => {
    if (form.type === 'TIERED' && !form.currencyMode) {
      setError('Choose how the spend thresholds apply to customers buying in other currencies.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const body = toBody(form);
      if (!form.id && form.bothStores) body.stores = ['A', 'B'];
      const saved = form.id ? { discounts: [await updateDiscount(form.id, body)] } : await createDiscount(body);
      onSaved(saved.discounts, !form.id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-accent/40 bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">{form.id ? 'Edit promotion' : 'New promotion'}</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-medium text-text-muted">
          Name
          <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Black Friday 2026: Stock up now" className={field} />
        </label>
        <div className="text-xs font-medium text-text-muted">
          Store
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {storeOptions.map((code) => (
              <button key={code} type="button" disabled={!!form.id} onClick={() => set({ store: code, bothStores: false, companyIds: [], excludedCompanyIds: [] })} className={`${chip(!form.bothStores && form.store === code)} disabled:opacity-60`}>
                {storeShort(code)}
              </button>
            ))}
            {!form.id && storeOptions.length > 1 && (
              <button type="button" onClick={() => set({ bothStores: true, companyIds: [], excludedCompanyIds: [] })} className={chip(form.bothStores)}>
                Both stores
              </button>
            )}
          </div>
        </div>
        <label className="block text-xs font-medium text-text-muted sm:col-span-2">
          Description <span className="font-normal">— shown to clients on their Promotions page, and used to draft the announcement email</span>
          <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2} maxLength={500} placeholder="e.g. Our biggest wholesale offer of the year — stock up on swim and underwear ahead of summer." className={field} />
        </label>
      </div>

      {/* Offer */}
      <div className="mt-4 flex gap-2">
        {['SINGLE', 'TIERED'].map((t) => (
          <button key={t} type="button" onClick={() => set({ type: t })} className={chip(form.type === t)}>
            {t === 'SINGLE' ? 'Single %' : 'Tiered by order value'}
          </button>
        ))}
      </div>
      {form.type === 'SINGLE' ? (
        <label className="mt-3 block max-w-[200px] text-xs font-medium text-text-muted">
          Discount %
          <input type="number" min={0} max={100} step="0.5" value={form.percent} onChange={(e) => set({ percent: e.target.value })} className={field} />
        </label>
      ) : (
        <div className="mt-3">
          <div className="text-xs font-medium text-text-muted">Tiers — the highest tier the order value reaches applies to the whole order</div>
          <div className="mt-1.5 space-y-2">
            {form.tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="text-text-muted">Order ≥</span>
                <input type="number" min={0} step="100" value={t.minSubtotal} onChange={(e) => setTier(i, { minSubtotal: e.target.value })} placeholder="0" className="w-28 rounded-md border border-line px-2 py-1.5 text-sm" />
                <span className="text-text-muted">{form.bothStores ? 'store currency' : CUR[form.store]} →</span>
                <input type="number" min={0} max={100} step="0.5" value={t.percent} onChange={(e) => setTier(i, { percent: e.target.value })} placeholder="%" className="w-20 rounded-md border border-line px-2 py-1.5 text-sm" />
                <span className="text-text-muted">%</span>
                {form.tiers.length > 1 && (
                  <button type="button" onClick={() => set({ tiers: form.tiers.filter((_, j) => j !== i) })} className="text-error hover:underline">
                    remove
                  </button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => set({ tiers: [...form.tiers, { minSubtotal: '', percent: '' }] })} className="mt-2 text-sm font-medium text-accent hover:underline">
            + Add tier
          </button>
          {form.bothStores && <p className="mt-1 text-xs text-text-muted">Both stores: the same numbers are used as AUD in Australia and USD in International.</p>}

          <fieldset className="mt-4">
            <legend className="text-xs font-medium text-text-muted">Customers buying in another currency — how should these thresholds apply?</legend>
            <div className="mt-1.5 space-y-1.5">
              {[
                ['CONVERT', 'Convert at today’s rate', 'e.g. a 500 threshold is the same real value in GBP, EUR or CAD'],
                ['SAME', 'Same number in every currency', 'e.g. a 500 threshold means 500 USD, 500 GBP and 500 CAD alike'],
              ].map(([mode, label, hint]) => (
                <label key={mode} className="flex cursor-pointer items-start gap-2 text-sm">
                  <input type="radio" name="currencyMode" checked={form.currencyMode === mode} onChange={() => set({ currencyMode: mode })} className="mt-0.5 accent-accent" />
                  <span>
                    <span className="text-text-primary">{label}</span>
                    <span className="block text-xs text-text-muted">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          {stores.map((s) => (
            <ThresholdTable key={s} form={form} store={s} />
          ))}
        </div>
      )}

      {/* Audience */}
      <div className="mt-5 text-xs font-medium text-text-muted">Who gets it</div>
      <div className="mt-1.5 flex flex-wrap gap-2">
        <button type="button" onClick={() => set({ allCustomers: false })} className={chip(!form.allCustomers)}>Selected customers</button>
        <button type="button" onClick={() => set({ allCustomers: true })} className={chip(form.allCustomers)}>
          All customers{form.bothStores ? '' : ` of ${storeShort(form.store)}`}
        </button>
      </div>
      <div className="mt-2 rounded-md border border-line">
        {form.allCustomers ? (
          <>
            <div className="border-b border-line px-3 py-2 text-xs text-text-muted">Leave anyone out? Tick customers to exclude — everyone else (including customers added later) gets it.</div>
            <CompanyChecklist
              companies={storeCompanies}
              selected={form.excludedCompanyIds}
              onToggle={(id) => toggleIn('excludedCompanyIds', id)}
              onClear={() => set({ excludedCompanyIds: [] })}
              showStore={form.bothStores}
            />
          </>
        ) : (
          <>
            <SegmentBuilder
              stores={stores}
              companies={storeCompanies}
              onAdd={(ids, label) => set({ companyIds: [...new Set([...form.companyIds, ...ids])], audienceLabel: form.companyIds.length ? '' : label })}
            />
            <CompanyChecklist
              companies={storeCompanies.filter((c) => c.isActive || form.companyIds.includes(c.id))}
              selected={form.companyIds}
              onToggle={(id) => set({ companyIds: form.companyIds.includes(id) ? form.companyIds.filter((x) => x !== id) : [...form.companyIds, id], audienceLabel: '' })}
              onSelectAll={(ids) => set({ companyIds: [...new Set([...form.companyIds, ...ids])], audienceLabel: '' })}
              onClear={() => set({ companyIds: [], audienceLabel: '' })}
              showStore={form.bothStores}
            />
          </>
        )}
      </div>

      {/* Schedule */}
      <div className="mt-4 grid max-w-xl grid-cols-3 gap-3">
        <label className="block text-xs font-medium text-text-muted">
          Starts
          <input type="date" value={form.startDate} onChange={(e) => set({ startDate: e.target.value })} className={field} />
        </label>
        <label className="block text-xs font-medium text-text-muted">
          Ends
          <input type="date" value={form.endDate} onChange={(e) => set({ endDate: e.target.value })} className={field} />
        </label>
        <label className="block text-xs font-medium text-text-muted">
          Time zone
          <select value={form.timeZone} onChange={(e) => set({ timeZone: e.target.value })} className={field}>
            {ZONES.map(([z, l]) => (
              <option key={z} value={z}>{l}</option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-1 text-xs text-text-muted">Starts at midnight and ends at 11:59 pm {zoneLabel(form.timeZone)} time. Leave blank to start now / run until you pause it.</p>

      <PreviewBox form={form} companies={storeCompanies} />

      {error && <p className="mt-3 text-sm text-error">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
          {busy ? 'Saving…' : form.id ? 'Save changes' : form.bothStores ? 'Create in both stores' : 'Create promotion'}
        </button>
        <button onClick={onCancel} className="rounded-md border border-line px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised">
          Cancel
        </button>
      </div>
    </div>
  );
}

// --- Results panel --------------------------------------------------------------------------------
function ReportPanel({ id, onEmail, canEmail }) {
  const [r, setR] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    fetchDiscountReport(id).then(setR).catch((e) => setR({ error: errMsg(e) }));
    if (canEmail) fetchPromotionCampaigns(id).then(setCampaigns).catch(() => setCampaigns([]));
  }, [id, canEmail]);
  if (!r) return <div className="mt-3 text-sm text-text-muted">Loading results…</div>;
  if (r.error) return <div className="mt-3 text-sm text-error">{r.error}</div>;
  const byCur = (m) => Object.entries(m).map(([c, a]) => fmtMoney(a, c)).join(' + ');
  const multi = (m) => Object.keys(m).length > 1;
  const aovChange = r.averageOrder.during && r.averageOrder.before90Days ? Math.round(((r.averageOrder.during - r.averageOrder.before90Days) / r.averageOrder.before90Days) * 100) : null;
  const maxTier = Math.max(1, ...r.tiers.map((t) => t.orders));
  const Stat = ({ label, value, sub }) => (
    <div className="rounded-md border border-line p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums text-text-primary">{value}</div>
      {sub && <div className="text-xs text-text-muted">{sub}</div>}
    </div>
  );
  return (
    <div className="mt-4 border-t border-line pt-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Orders" value={r.orders} sub={`${r.clientsOrdered} of ${r.clientsTargeted} clients`} />
        <Stat label="Revenue (after discount)" value={fmtMoney(r.revenue.total, r.currency)} sub={multi(r.revenue.byCurrency) ? byCur(r.revenue.byCurrency) : null} />
        <Stat label="Discount given" value={fmtMoney(r.discountGiven.total, r.currency)} sub={multi(r.discountGiven.byCurrency) ? byCur(r.discountGiven.byCurrency) : null} />
        <Stat
          label="Average order"
          value={r.averageOrder.during != null ? fmtMoney(r.averageOrder.during, r.currency) : '—'}
          sub={aovChange != null ? `${aovChange >= 0 ? '+' : ''}${aovChange}% vs their previous 90 days` : r.orders ? 'No earlier orders to compare' : null}
        />
      </div>
      {r.tiers.length > 1 && (
        <div className="mt-4">
          <div className="text-xs font-medium text-text-muted">Orders by tier</div>
          <div className="mt-1.5 space-y-1">
            {r.tiers.map((t) => (
              <div key={t.percent} className="flex items-center gap-2 text-sm">
                <span className="w-36 shrink-0 tabular-nums text-text-secondary">≥ {fmtWhole(t.minSubtotal, r.currency)} · {t.percent}%</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-raised">
                  <div className="h-full rounded-full bg-accent" style={{ width: `${(t.orders / maxTier) * 100}%` }} />
                </div>
                <span className="w-8 text-right tabular-nums text-text-primary">{t.orders}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs font-medium text-text-muted">Haven’t ordered under this promotion yet ({r.notOrdered.length})</div>
          {canEmail && r.notOrdered.length > 0 && (
            <button onClick={() => onEmail('REMINDER')} className="text-xs font-medium text-accent hover:underline">Send them a reminder email →</button>
          )}
        </div>
        {r.notOrdered.length === 0 ? (
          <p className="mt-1 text-sm text-text-secondary">Every targeted client has ordered.</p>
        ) : (
          <div className="mt-1.5 max-h-60 overflow-y-auto rounded-md border border-line">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface-raised text-left text-xs text-text-muted">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Client</th>
                  <th className="px-3 py-1.5 font-medium">Rep</th>
                  <th className="px-3 py-1.5 font-medium">Last order</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {r.notOrdered.map((c) => (
                  <tr key={c.id}>
                    <td className="px-3 py-1.5 text-text-primary">{c.name}</td>
                    <td className="px-3 py-1.5 text-text-secondary">{c.reps.join(', ') || '—'}</td>
                    <td className="px-3 py-1.5 text-text-muted">{c.lastOrderAt ? new Date(c.lastOrderAt).toLocaleDateString() : 'never'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {canEmail && campaigns.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium text-text-muted">Emails about this promotion</div>
          <ul className="mt-1 space-y-1 text-sm">
            {campaigns.map((c) => (
              <li key={c.id}>
                <Link to={`/marketing/${c.id}`} className="text-accent hover:underline">{c.subject || c.name}</Link>
                <span className="text-xs text-text-muted"> · {c.type === 'REMINDER' ? 'reminder' : 'announcement'} · {c.status === 'SENT' ? `sent ${new Date(c.sentAt).toLocaleDateString()}` : c.status.toLowerCase()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// After creating: offer the announcement email (reviewed in Marketing before anything is sent).
function AnnounceDialog({ created, onAnnounce, onClose }) {
  const n = (d) => (d.allCustomers ? 'all customers' : `${d.companies.length} customer${d.companies.length === 1 ? '' : 's'}`);
  return (
    <div className="mb-4 rounded-lg border border-accent/40 bg-accent-light p-4 shadow-sm">
      <div className="text-sm font-semibold text-text-primary">Promotion created. Let your customers know?</div>
      <p className="mt-1 text-sm text-text-secondary">
        We’ll draft an announcement email from “{created[0].name}” — offer, tiers in each client’s currency and dates — for you to review, test and send from Marketing. Nothing goes out until you send it.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {created.map((d) => (
          <button key={d.id} onClick={() => onAnnounce(d, 'PROMOTION')} className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover">
            Create announcement email{created.length > 1 ? ` — ${storeShort(d.store)}` : ''} ({n(d)})
          </button>
        ))}
        <button onClick={onClose} className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">Not now</button>
      </div>
    </div>
  );
}

const rowToForm = (d) => ({
  ...BLANK,
  id: d.id,
  store: d.store,
  name: d.name,
  description: d.description ?? '',
  type: d.type,
  percent: d.percent ?? '',
  tiers: d.tiers?.length ? d.tiers.map((t) => ({ minSubtotal: t.minSubtotal, percent: t.percent })) : [{ minSubtotal: '', percent: '' }],
  currencyMode: d.type === 'TIERED' ? (d.thresholdCurrency ? 'CONVERT' : 'SAME') : '',
  allCustomers: d.allCustomers,
  companyIds: d.companies.map((c) => c.id),
  excludedCompanyIds: d.exclusions.map((c) => c.id),
  audienceLabel: d.audienceLabel ?? '',
  timeZone: d.timeZone || 'Australia/Sydney',
  startDate: d.startDate || '',
  endDate: d.endDate || '',
  active: d.active,
});

export default function Discounts() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const editable = can(user, CAP.MANAGE_PAYMENTS);
  const canEmail = can(user, CAP.MANAGE_MARKETING);
  const [announce, setAnnounce] = useState(null); // promotions just created → offer the email
  const storeOptions = allowedStoreCodes(user) || ['A', 'B'];
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form object or null
  const [tab, setTab] = useState('current'); // current | archived
  const [openReport, setOpenReport] = useState(null);
  const [msg, setMsg] = useState('');
  const topRef = useRef(null);

  const load = () => {
    setLoading(true);
    fetchDiscounts().then(setList).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    fetchCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  const startEdit = (f) => {
    setEditing(f);
    setMsg('');
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const onSaved = (saved, created) => {
    setEditing(null);
    setMsg(created ? '' : 'Saved.');
    if (created && canEmail) setAnnounce(saved);
    else if (created) setMsg(`Created ${saved.map((d) => `“${d.name}” (${storeShort(d.store)})`).join(' and ')}.`);
    load();
  };
  // Draft an announcement / reminder in Marketing for this promotion and open it there.
  const emailAbout = async (d, type) => {
    try {
      const c = await createCampaign({ type, discountId: d.id });
      navigate(`/marketing/${c.id}`);
    } catch (e) {
      setMsg(errMsg(e));
    }
  };
  const act = (fn) => fn().then(load).catch((e) => setMsg(errMsg(e)));

  const describe = (d) => {
    if (d.type === 'SINGLE') return `${d.percent}% off every order`;
    const tiers = d.tiers.map((t) => `≥${fmtWhole(t.minSubtotal, CUR[d.store])}: ${t.percent}%`).join(' · ');
    return `${tiers} · ${d.thresholdCurrency ? 'converted for other currencies' : 'same number in every currency'}`;
  };
  const audience = (d) => {
    if (d.allCustomers) return d.exclusions.length ? `All customers except ${d.exclusions.length}` : 'All customers';
    const n = `${d.companies.length} customer${d.companies.length === 1 ? '' : 's'}`;
    return d.audienceLabel ? `${n} · ${d.audienceLabel}` : n;
  };

  const shown = list.filter((d) => (tab === 'archived' ? d.archivedAt : !d.archivedAt));
  const archivedCount = list.filter((d) => d.archivedAt).length;

  return (
    <>
      <div ref={topRef} />
      <PageHeader
        title="Promotions"
        subtitle="Percentage offers for customers — one rate or tiered by order value — applied automatically when they order. Announce them from Marketing."
        actions={
          editable && !editing ? (
            <button onClick={() => startEdit({ ...BLANK, store: storeOptions[0] })} className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover">
              New promotion
            </button>
          ) : null
        }
      />

      {msg && <div className="mb-4 rounded-md bg-accent-light px-3 py-2 text-sm text-text-secondary">{msg}</div>}
      {announce && <AnnounceDialog created={announce} onAnnounce={emailAbout} onClose={() => setAnnounce(null)} />}
      {editing && (
        <DiscountForm key={editing.id || 'new'} initial={editing} companies={companies} storeOptions={storeOptions} onSaved={onSaved} onCancel={() => setEditing(null)} />
      )}

      <div className="mb-3 flex gap-2">
        <button onClick={() => setTab('current')} className={chip(tab === 'current')}>Current</button>
        <button onClick={() => setTab('archived')} className={chip(tab === 'archived')}>Archived{archivedCount ? ` (${archivedCount})` : ''}</button>
      </div>

      {loading ? (
        <div className="text-sm text-text-muted">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          {tab === 'archived' ? 'No archived promotions.' : `No promotions yet.${editable ? ' Click New promotion to create one.' : ''}`}
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((d) => {
            const st = promoStatus(d);
            return (
              <div key={d.id} className="rounded-lg border border-line bg-surface p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-text-primary">{d.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                      <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-text-secondary">{storeShort(d.store)}</span>
                    </div>
                    <div className="mt-1 text-sm text-text-secondary">{describe(d)}</div>
                    <div className="mt-1 text-xs text-text-muted">
                      {audience(d)} · {scheduleLabel(d)} · {d.orderCount} order{d.orderCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-3 text-sm">
                    <button onClick={() => setOpenReport(openReport === d.id ? null : d.id)} className="font-medium text-accent hover:underline">
                      {openReport === d.id ? 'Hide results' : 'Results'}
                    </button>
                    {canEmail && ['live', 'scheduled'].includes(st.key) && (
                      <>
                        <button onClick={() => emailAbout(d, 'PROMOTION')} className="text-text-secondary hover:text-text-primary">Announce</button>
                        {st.key === 'live' && <button onClick={() => emailAbout(d, 'REMINDER')} className="text-text-secondary hover:text-text-primary">Send reminder</button>}
                      </>
                    )}
                    {editable && !d.archivedAt && (
                      <>
                        <button onClick={() => startEdit(rowToForm(d))} className="text-text-secondary hover:text-text-primary">Edit</button>
                        <button onClick={() => act(() => setDiscountActive(d.id, !d.active))} className="text-text-secondary hover:text-text-primary">{d.active ? 'Pause' : 'Resume'}</button>
                      </>
                    )}
                    {editable && (
                      <button onClick={() => startEdit({ ...rowToForm(d), id: null, name: `${d.name} (copy)`, startDate: '', endDate: '', active: true })} className="text-text-secondary hover:text-text-primary">
                        Duplicate
                      </button>
                    )}
                    {editable &&
                      (d.archivedAt ? (
                        <>
                          <button onClick={() => act(() => setDiscountArchived(d.id, false))} className="text-text-secondary hover:text-text-primary">Restore</button>
                          {d.orderCount === 0 && (
                            <button
                              onClick={() => window.confirm(`Delete “${d.name}” permanently? No order used it.`) && act(() => deleteDiscount(d.id))}
                              className="text-error hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          onClick={() => window.confirm(`Archive “${d.name}”? It stops applying to new orders; its results stay here under Archived.`) && act(() => setDiscountArchived(d.id, true))}
                          className="text-text-secondary hover:text-error"
                        >
                          Archive
                        </button>
                      ))}
                  </div>
                </div>
                {openReport === d.id && <ReportPanel id={d.id} canEmail={canEmail} onEmail={(type) => emailAbout(d, type)} />}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
